interface SignedUpload {
  apiKey: string;
  cloudName: string;
  params: Record<string, string>;
  resourceType: string;
  signature: string;
  timestamp: number;
}

interface UploadArgs {
  collectionSlug: string;
  file: File;
  mimeType: string;
  needsTenant: boolean;
  signUrl: string;
  tenantId: number | string | undefined;
}

const NO_TENANT_MESSAGE = 'No Landing is selected, so this file has nowhere to go. Pick one in the Landing selector and try again.';

export interface CloudinaryUploadContext {
  publicId: string;
  resourceType: string;
}

/**
 * The reason a refusal gives, in words an editor can act on. The body is read as text first: an error
 * from the ingress rather than from an API is an HTML page, and parsing that as JSON would throw a
 * `SyntaxError` over the failure we are trying to report.
 *
 * Cloudinary also states its reason in the `x-cld-error` header, but it sends no
 * `access-control-expose-headers`, so the body is the only copy a browser can read.
 */
const refusalMessage = async (response: Response, read: (body: object) => string | undefined, fallback: string): Promise<string> => {
  const body = await response.text();

  try {
    const parsed: unknown = JSON.parse(body);
    const described = parsed !== null && typeof parsed === 'object' && read(parsed);

    if (described) return described;
  } catch {
    // Not JSON.
  }

  return `${fallback} (HTTP ${response.status}).`;
};

/** Payload lists its reasons under `errors`. */
const payloadRefusal = (body: object): string | undefined =>
  (body as { errors?: { message?: string }[] }).errors
    ?.map(({ message }) => message)
    .filter((message) => Boolean(message))
    .join(', ');

const cloudinaryRefusal = (body: object): string | undefined => (body as { error?: { message?: string } }).error?.message;

const isSignedUpload = (signed: Partial<SignedUpload>): signed is SignedUpload =>
  Boolean(signed.apiKey && signed.cloudName && signed.params && signed.resourceType && signed.signature && signed.timestamp);

const signUpload = async ({ collectionSlug, file, mimeType, needsTenant, signUrl, tenantId }: UploadArgs): Promise<SignedUpload> => {
  if (needsTenant && !tenantId) throw new Error(NO_TENANT_MESSAGE);

  const response = await fetch(signUrl, {
    body: JSON.stringify({ collectionSlug, filename: file.name, filesize: file.size, mimeType, tenantId }),
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
  });

  if (!response.ok) {
    throw new Error(await refusalMessage(response, payloadRefusal, 'Could not authorize the upload'));
  }

  const signed = (await response.json()) as Partial<SignedUpload>;

  // An admin tab older than the server it talks to gets an answer without these. A missing
  // `cloudName` or `resourceType` makes the upload URL read `.../undefined/upload`, and a missing
  // `params` throws on `Object.entries`. Name what came back: the cause is never the file.
  if (!isSignedUpload(signed)) {
    throw new Error(
      `The signing endpoint did not answer with everything needed to upload. It answered with: ${Object.keys(signed).join(', ') || 'nothing'}. Reload the admin panel and try again.`,
    );
  }

  return signed;
};

/**
 * Signs the upload on our server, then sends the file straight to Cloudinary from the browser.
 *
 * The second request is the whole point of this feature: it goes to `api.cloudinary.com` and never
 * touches the ALB, the shared nginx ingress or our pod. Only the signature request and the later
 * document create cross our infrastructure, and both are a few hundred bytes.
 *
 * `CLOUDINARY_API_SECRET` stays on the server; the browser only ever receives the public api key and
 * a signature over params it cannot change.
 *
 * The resource type is stated in the URL rather than left to `/auto/upload`, so the stored asset is
 * the one we decided on rather than the one Cloudinary guessed. It is safe to take from the
 * signature: the server derives it from the same declaration it derives the format list from.
 */
export const uploadToCloudinary = async (args: UploadArgs): Promise<CloudinaryUploadContext> => {
  const { apiKey, cloudName, params, resourceType: signedResourceType, signature, timestamp } = await signUpload(args);

  const body = new FormData();

  for (const [key, value] of Object.entries(params)) {
    body.append(key, value);
  }

  body.append('api_key', apiKey);
  body.append('timestamp', String(timestamp));
  body.append('signature', signature);
  body.append('file', args.file);

  const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/${signedResourceType}/upload`, { body, method: 'POST' });

  if (!response.ok) {
    throw new Error(await refusalMessage(response, cloudinaryRefusal, 'The upload to Cloudinary failed'));
  }

  const { public_id: publicId, resource_type: resourceType } = (await response.json()) as Partial<
    Record<'public_id' | 'resource_type', string>
  >;

  if (!publicId || !resourceType) {
    throw new Error('Cloudinary did not return an asset id.');
  }

  return { publicId, resourceType };
};
